import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography,
  Box,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import VenueForm from '../components/VenueForm';
import { 
  addDoc, 
  collection, 
  serverTimestamp, 
  updateDoc, 
  doc 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { logger } from "../utils/logger"; // Adjust the import path as necessary

const AddVenuePage = () => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  // Parse state from URL
  const { initialSearchQuery, dealDescription, suggestionId } = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const stateParam = params.get('state');
      
      if (stateParam) {
        const decoded = JSON.parse(atob(stateParam));
        logger.info('Decoded state:', decoded);
        return decoded;
      }
      return {};
    } catch (error) {
      logger.error('Error parsing state:', error);
      return {};
    }
  })();

  const [formData, setFormData] = useState({
    headline: '',
    address: '',
    phone: '',
    link: '',
    category: 'hh',
    lat: '',
    lon: '',
    is_active: true,
    main_offer: dealDescription || '', // Pre-fill deal description if available
    offer_details: '',
    place_id: '',
    opening_hours: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    }
  });

  const handleSubmit = async (venueData) => {
    try {
      setLoading(true);
      
      const venueToSave = {
        ...venueData,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        category: 'hh',
        is_active: true
      };
      
      const docRef = await addDoc(collection(db, 'venues'), venueToSave);
      
      if (suggestionId) {
        await updateDoc(doc(db, 'deal_suggestions', suggestionId), {
          status: 'complete'
        });
      }

      setAlert({
        open: true,
        message: 'Venue added successfully!',
        severity: 'success'
      });

      setTimeout(() => navigate('/admin'), 2000);
    } catch (error) {
      logger.error('Error in handleSubmit:', error);
      setAlert({
        open: true,
        message: 'Failed to add venue. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadAndStoreImage = async (photoReference) => {
    if (!photoReference) {
      logger.warn('No photo reference provided');
      return null;
    }

    try {
      logger.log('Starting image download for photo reference:', photoReference);
      
      // Construct the Places Photo API URL
      const photoUrl = `https://maps.googleapis.com/maps/api/place/photo?maxwidth=1200&photoreference=${photoReference}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
      
      // Download the image
      const response = await fetch(photoUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      
      // Convert to blob
      const blob = await response.blob();
      
      // Generate unique filename
      const fileName = `venue-images/${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`;
      const storageRef = ref(storage, fileName);
      
      // Upload to Firebase Storage
      logger.log('Uploading to Firebase Storage...');
      await uploadBytes(storageRef, blob);
      
      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);
      logger.log('Upload complete, URL:', downloadUrl);
      
      return {
        image_url: downloadUrl,
        photo_reference: photoReference
      };
    } catch (error) {
      logger.error('Error downloading/storing image:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  const handleGooglePlaceSelected = (placeData) => {
    logger.log('Google place data received:', placeData);
    setFormData(prevData => ({
      ...prevData,
      ...placeData,
      category: 'hh', // Ensure category remains set
      is_active: true, // Ensure is_active remains set
    }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {loading && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          backgroundColor: 'rgba(255, 255, 255, 0.7)', 
          zIndex: 9999 
        }}>
          <CircularProgress />
        </Box>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Add New Venue
        </Typography>
        
        <VenueForm
          mode="add"
          venue={formData}
          initialSearchQuery={initialSearchQuery} // Pass the search query directly
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onGooglePlaceSelected={handleGooglePlaceSelected}
        />
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddVenuePage;